import { useState, useEffect, useRef } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { QrCode, Upload, CheckCircle, X, Users, Lock, ScanLine, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { logAttendanceScan, logAttendanceScanBulk, resolveEaAttendance } from "@/lib/attendanceScanLog";

export default function EAAttendance() {
  const [bulkAbsentIds, setBulkAbsentIds] = useState("");
  const [bulkPresentIds, setBulkPresentIds] = useState("");
  const [bulkExcusedEmails, setBulkExcusedEmails] = useState("");
  const [message, setMessage] = useState(null);
  const [showEACloseoutConfirm, setShowEACloseoutConfirm] = useState(false);
  const [showASDCloseoutConfirm, setShowASDCloseoutConfirm] = useState(false);
  const [isKioskMode, setIsKioskMode] = useState(false);
  const [scanInput, setScanInput] = useState("");
  const [scanHistory, setScanHistory] = useState([]);
  const [welcomeBanner, setWelcomeBanner] = useState(null); // { name, otherId } or null
  const scanInputRef = useRef(null);
  const welcomeTimerRef = useRef(null);
  const queryClient = useQueryClient();

  const { data: rosterEntries = [] } = useQuery({
    queryKey: ['rosterEntries'],
    queryFn: () => base44.entities.RosterEntry.list()
  });

  // Filter for EA roster only (active entries, includes all attendance statuses)
  const eaEntries = rosterEntries.filter((e) => e.status === 'active' || e.status === 'excused');

  const markPresentBulkMutation = useMutation({
    mutationFn: async (otherIds) => {
      const ids = otherIds.split('\n').map((id) => id.trim()).filter((id) => id);
      const normalizeId = (id) => id.toString().trim().replace(/^0+/, '') || '0';
      
      // Collect all operations
      const entriesToUpdate = [];
      const logsToCreate = [];
      const namesMarked = new Set();
      const trackingUpdates = [];

      // First collect all student IDs that need tracking lookup
      const studentIdsNeedingTracking = new Set();
      
      for (const otherId of ids) {
        const normalizedInput = normalizeId(otherId);
        
        const entries = eaEntries.filter((e) => {
          const normalizedDbId = normalizeId(e.other_id || '');
          return normalizedDbId === normalizedInput && e.attendance_status === 'pending';
        });

        entries.forEach(entry => {
          entriesToUpdate.push(entry.id);
          logsToCreate.push({
            student_id: entry.other_id,
            student_name: `${entry.student_first_name} ${entry.student_last_name}`,
            roster_entry_id: entry.id,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            status: 'present',
            scanned: false,
            method: 'manual_present'
          });
          namesMarked.add(`${entry.student_first_name} ${entry.student_last_name}`);
          studentIdsNeedingTracking.add(entry.other_id);
        });
      }

      // Fetch ALL tracking records at once instead of one by one
      const allTrackingRecords = studentIdsNeedingTracking.size > 0 
        ? await base44.entities.EAMissTracking.filter({ status: 'active' })
        : [];
      
      // Filter to only the students we care about
      const relevantTracking = allTrackingRecords.filter(t => 
        studentIdsNeedingTracking.has(t.student_id)
      );

      // Prepare tracking updates
      for (const tracking of relevantTracking) {
        const remaining = (tracking.makeup_days_owed || 0) - (tracking.makeup_days_completed || 0);
        
        if (remaining > 0) {
          const newCompleted = (tracking.makeup_days_completed || 0) + 1;
          let makeupDates = [];
          try {
            makeupDates = tracking.makeup_dates ? JSON.parse(tracking.makeup_dates) : [];
          } catch (e) {
            makeupDates = [];
          }
          makeupDates.push(format(new Date(), 'yyyy-MM-dd'));
          const allComplete = newCompleted >= (tracking.makeup_days_owed || 0);

          trackingUpdates.push({
            id: tracking.id,
            data: {
              makeup_days_completed: newCompleted,
              makeup_dates: JSON.stringify(makeupDates),
              status: allComplete ? 'resolved' : 'active'
            }
          });
        } else {
          trackingUpdates.push({
            id: tracking.id,
            data: {
              status: 'resolved',
              intervention_notes: (tracking.intervention_notes || '') + `\n[${format(new Date(), 'yyyy-MM-dd')}] Student attended EA - consequences reset and case archived.`
            }
          });
        }
      }

      // Execute all updates in EXTREMELY SMALL batches with very long delays (handles 150+ students)
      const batchSize = 3;
      
      // Update roster entries
      for (let i = 0; i < entriesToUpdate.length; i += batchSize) {
        const batch = entriesToUpdate.slice(i, i + batchSize);
        await Promise.all(batch.map(id => 
          base44.entities.RosterEntry.update(id, { attendance_status: 'present' })
        ));
        console.log(`✅ Updated ${Math.min(i + batchSize, entriesToUpdate.length)}/${entriesToUpdate.length} entries`);
        // Very long delay between batches to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Delay before bulk create
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create logs in smaller chunks to avoid bulk create limits
      if (logsToCreate.length > 0) {
        const logChunkSize = 50;
        for (let i = 0; i < logsToCreate.length; i += logChunkSize) {
          const chunk = logsToCreate.slice(i, i + logChunkSize);
          await base44.entities.AttendanceLog.bulkCreate(chunk);
          if (i + logChunkSize < logsToCreate.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        // Mirror to AttendanceScanLog (audit-only, fire-and-forget)
        logAttendanceScanBulk(logsToCreate.map((l) => ({
          studentId: l.student_id,
          studentName: l.student_name,
          rosterType: 'ea',
          status: l.status,
          method: 'manual_entry',
        })));
        // Server-side hook: credit EA-present logs against ea_miss_trackings
        // so active EA cases resolve automatically.
        logsToCreate
          .filter((l) => l.status === 'present')
          .forEach((l) => resolveEaAttendance(l.student_id, l.log_date));
      }

      // Delay before next operation
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update tracking records (extremely small batches)
      const trackingBatchSize = 3;
      for (let i = 0; i < trackingUpdates.length; i += trackingBatchSize) {
        const batch = trackingUpdates.slice(i, i + trackingBatchSize);
        await Promise.all(batch.map(update => 
          base44.entities.EAMissTracking.update(update.id, update.data)
        ));
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      return { count: entriesToUpdate.length, names: Array.from(namesMarked), makeupTracked: trackingUpdates.length };
    },
    onSuccess: ({ count, names, makeupTracked }) => {
      let successMessage = `${count} EA assignment(s) marked as PRESENT.`;
      if (names.length > 0) {
        successMessage += ` Students: ${names.join(', ')}`;
      }
      if (makeupTracked > 0) {
        successMessage += ` | ✅ ${makeupTracked} makeup day(s) automatically tracked!`;
      }
      setMessage({ type: 'success', text: successMessage });
      setBulkPresentIds("");
      queryClient.invalidateQueries(['rosterEntries']);
      queryClient.invalidateQueries(['eaMissTracking']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || "Failed to mark students present" });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  // Barcode / rapid single-scan mark-present (keyboard wedge scanner support)
  const scanPresentMutation = useMutation({
    mutationFn: async (rawId) => {
      const normalizeId = (id) => id.toString().trim().replace(/^0+/, '') || '0';
      const scanned = rawId.toString().trim();
      if (!scanned) throw new Error("Empty scan");

      const normalizedInput = normalizeId(scanned);
      const entries = eaEntries.filter((e) => {
        const normalizedDbId = normalizeId(e.other_id || '');
        return normalizedDbId === normalizedInput && e.attendance_status === 'pending';
      });

      if (entries.length === 0) {
        throw new Error(`No pending EA entry for ID ${scanned}`);
      }

      const studentName = `${entries[0].student_first_name} ${entries[0].student_last_name}`;

      for (const entry of entries) {
        await base44.entities.RosterEntry.update(entry.id, { attendance_status: 'present' });
        await base44.entities.AttendanceLog.create({
          student_id: entry.other_id,
          student_name: studentName,
          roster_entry_id: entry.id,
          log_date: format(new Date(), 'yyyy-MM-dd'),
          status: 'present',
          scanned: true,
          method: 'barcode_scan'
        });
        // Also record to the audit-only AttendanceScanLog collection.
        logAttendanceScan({
          studentId: entry.other_id,
          studentName,
          rosterType: 'ea',
          status: 'present',
          method: 'barcode_scan',
        });
        // Server-side hook: credit the EA present against ea_miss_trackings.
        resolveEaAttendance(entry.other_id);
      }

      // Advance makeup tracking if applicable
      try {
        const tracking = await base44.entities.EAMissTracking.filter({
          student_id: entries[0].other_id,
          status: 'active'
        });
        for (const t of tracking) {
          const remaining = (t.makeup_days_owed || 0) - (t.makeup_days_completed || 0);
          if (remaining > 0) {
            const newCompleted = (t.makeup_days_completed || 0) + 1;
            let makeupDates = [];
            try { makeupDates = t.makeup_dates ? JSON.parse(t.makeup_dates) : []; } catch (e) { makeupDates = []; }
            makeupDates.push(format(new Date(), 'yyyy-MM-dd'));
            const allComplete = newCompleted >= (t.makeup_days_owed || 0);
            await base44.entities.EAMissTracking.update(t.id, {
              makeup_days_completed: newCompleted,
              makeup_dates: JSON.stringify(makeupDates),
              status: allComplete ? 'resolved' : 'active'
            });
          }
        }
      } catch (e) {
        console.warn("Makeup tracking update skipped:", e);
      }

      return { studentName, otherId: scanned, count: entries.length };
    },
    onSuccess: ({ studentName, otherId, count }) => {
      setScanHistory(prev => [
        { ts: new Date(), otherId, studentName, status: 'ok', count },
        ...prev.slice(0, 19),
      ]);
      // Show "Welcome, [Student Name]" banner for 2 seconds, then return to ready state
      setWelcomeBanner({ name: studentName, otherId });
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
      welcomeTimerRef.current = setTimeout(() => setWelcomeBanner(null), 2000);
      queryClient.invalidateQueries(['rosterEntries']);
      queryClient.invalidateQueries(['eaMissTracking']);
    },
    onError: (err, rawId) => {
      setScanHistory(prev => [
        { ts: new Date(), otherId: rawId, studentName: null, status: 'error', message: err.message },
        ...prev.slice(0, 19),
      ]);
      // Briefly surface the error inline too, then return to ready state
      setWelcomeBanner({ name: null, otherId: rawId, error: err.message });
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
      welcomeTimerRef.current = setTimeout(() => setWelcomeBanner(null), 2000);
    },
  });

  const handleScanSubmit = (e) => {
    e.preventDefault();
    const value = scanInput.trim();
    if (!value) return;
    scanPresentMutation.mutate(value);
    setScanInput("");
  };

  // Auto-submit only when EXACTLY 6 digits have been entered. Barcode scanners
  // still push all 6 characters in one burst so the debounce is instant for
  // them; manual typing no longer fires after the first digit.
  useEffect(() => {
    const trimmed = scanInput.trim();
    if (!trimmed) return;
    if (scanPresentMutation.isPending) return;
    if (!/^\d{6}$/.test(trimmed)) return;
    const t = setTimeout(() => {
      scanPresentMutation.mutate(trimmed);
      setScanInput("");
    }, 100);
    return () => clearTimeout(t);
  }, [scanInput, scanPresentMutation]);

  // Clear the welcome timer on unmount to avoid leaking
  useEffect(() => {
    return () => {
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
    };
  }, []);

  // Refocus scan input whenever the welcome/error banner clears. useEffect runs AFTER
  // React commits the DOM (i.e. after disabled=false takes effect), which fixes the
  // Chromium no-op that setTimeout(...,0) hits under React 18 automatic batching.
  useEffect(() => {
    if (welcomeBanner) return;
    scanInputRef.current?.focus();
  }, [welcomeBanner]);

  const markAbsentBulkMutation = useMutation({
    mutationFn: async (otherIds) => {
      const ids = otherIds.split('\n').map((id) => id.trim()).filter((id) => id);
      const normalizeId = (id) => id.toString().trim().replace(/^0+/, '') || '0';
      
      // Collect all entries to update
      const entriesToUpdate = [];
      const logsToCreate = [];
      const namesMarked = new Set();

      for (const otherId of ids) {
        const normalizedInput = normalizeId(otherId);
        
        const entries = eaEntries.filter((e) => {
          const normalizedDbId = normalizeId(e.other_id || '');
          return normalizedDbId === normalizedInput && e.attendance_status === 'pending';
        });

        entries.forEach(entry => {
          entriesToUpdate.push(entry.id);
          logsToCreate.push({
            student_id: entry.other_id,
            student_name: `${entry.student_first_name} ${entry.student_last_name}`,
            roster_entry_id: entry.id,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            status: 'absent',
            scanned: false,
            method: 'manual_absent'
          });
          namesMarked.add(`${entry.student_first_name} ${entry.student_last_name}`);
        });
      }

      // Update all entries in EXTREMELY SMALL batches with very long delays (handles 150+ students)
      const batchSize = 3;
      for (let i = 0; i < entriesToUpdate.length; i += batchSize) {
        const batch = entriesToUpdate.slice(i, i + batchSize);
        await Promise.all(batch.map(id => 
          base44.entities.RosterEntry.update(id, { attendance_status: 'absent' })
        ));
        console.log(`✅ Updated ${Math.min(i + batchSize, entriesToUpdate.length)}/${entriesToUpdate.length} entries`);
        // Very long delay between batches to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Delay before bulk create
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create logs in smaller chunks to avoid bulk create limits
      if (logsToCreate.length > 0) {
        const logChunkSize = 50;
        for (let i = 0; i < logsToCreate.length; i += logChunkSize) {
          const chunk = logsToCreate.slice(i, i + logChunkSize);
          await base44.entities.AttendanceLog.bulkCreate(chunk);
          if (i + logChunkSize < logsToCreate.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        // Mirror to AttendanceScanLog (audit-only)
        logAttendanceScanBulk(logsToCreate.map((l) => ({
          studentId: l.student_id,
          studentName: l.student_name,
          rosterType: 'ea',
          status: l.status,
          method: 'manual_entry',
        })));
        // Server-side hook: credit EA-present logs against ea_miss_trackings
        // so active EA cases resolve automatically.
        logsToCreate
          .filter((l) => l.status === 'present')
          .forEach((l) => resolveEaAttendance(l.student_id, l.log_date));
      }

      return { count: entriesToUpdate.length, names: Array.from(namesMarked) };
    },
    onSuccess: ({ count, names }) => {
      let successMessage = `${count} EA assignment(s) marked as ABSENT.`;
      if (names.length > 0) {
        successMessage += ` Students: ${names.join(', ')}`;
      }
      setMessage({ type: 'success', text: successMessage });
      setBulkAbsentIds("");
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || "Failed to mark students absent" });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const markExcusedBulkMutation = useMutation({
    mutationFn: async (emails) => {
      const emailList = emails.split('\n').map((email) => email.trim().toLowerCase()).filter((email) => email);
      const entriesToUpdate = [];
      const logsToCreate = [];
      const namesMarked = new Set();
      const notFound = [];

      for (const email of emailList) {
        const entries = eaEntries.filter(
          (e) => e.student_email?.toString().trim().toLowerCase() === email &&
          (e.attendance_status === 'pending' || e.status === 'active')
        );

        if (entries.length === 0) {
          notFound.push(email);
          continue;
        }

        entries.forEach(entry => {
          entriesToUpdate.push(entry.id);
          logsToCreate.push({
            student_id: entry.other_id,
            student_name: `${entry.student_first_name} ${entry.student_last_name}`,
            roster_entry_id: entry.id,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            status: 'present',
            scanned: false,
            method: 'manual_present'
          });
          namesMarked.add(`${entry.student_first_name} ${entry.student_last_name}`);
        });
      }

      // Execute updates in EXTREMELY SMALL batches with very long delays (handles 150+ students)
      const batchSize = 3;
      for (let i = 0; i < entriesToUpdate.length; i += batchSize) {
        const batch = entriesToUpdate.slice(i, i + batchSize);
        await Promise.all(batch.map(id => 
          base44.entities.RosterEntry.update(id, { status: 'excused' })
        ));
        console.log(`✅ Updated ${Math.min(i + batchSize, entriesToUpdate.length)}/${entriesToUpdate.length} entries`);
        // Very long delay between batches to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Delay before bulk create
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create logs in smaller chunks to avoid bulk create limits
      if (logsToCreate.length > 0) {
        const logChunkSize = 50;
        for (let i = 0; i < logsToCreate.length; i += logChunkSize) {
          const chunk = logsToCreate.slice(i, i + logChunkSize);
          await base44.entities.AttendanceLog.bulkCreate(chunk);
          if (i + logChunkSize < logsToCreate.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        // Mirror to AttendanceScanLog (audit-only)
        logAttendanceScanBulk(logsToCreate.map((l) => ({
          studentId: l.student_id,
          studentName: l.student_name,
          rosterType: 'ea',
          status: l.status,
          method: 'manual_entry',
        })));
        // Server-side hook: credit EA-present logs against ea_miss_trackings
        // so active EA cases resolve automatically.
        logsToCreate
          .filter((l) => l.status === 'present')
          .forEach((l) => resolveEaAttendance(l.student_id, l.log_date));
      }

      return { count: entriesToUpdate.length, names: Array.from(namesMarked), notFound };
    },
    onSuccess: ({ count, names, notFound }) => {
      let successMessage = `${count} EA assignment(s) marked as EXCUSED.`;
      if (names.length > 0) {
        successMessage += ` Students: ${names.join(', ')}`;
      }
      if (notFound.length > 0) {
        successMessage += ` | ⚠️ Not found: ${notFound.join(', ')}`;
      }
      setMessage({ type: 'success', text: successMessage });
      setBulkExcusedEmails("");
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 8000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || "Failed to mark students excused" });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const processRefusalsMutation = useMutation({
    mutationFn: async () => {
      const pendingEntries = eaEntries.filter((e) => e.attendance_status === 'pending');
      
      const logsToCreate = pendingEntries.map(entry => ({
        student_id: entry.other_id,
        student_name: `${entry.student_first_name} ${entry.student_last_name}`,
        roster_entry_id: entry.id,
        log_date: format(new Date(), 'yyyy-MM-dd'),
        status: 'refusal',
        scanned: false,
        method: 'manual_refusal'
      }));

      // Update all entries in EXTREMELY SMALL batches with very long delays (handles 150+ students)
      const batchSize = 3;
      const entryIds = pendingEntries.map(e => e.id);
      
      for (let i = 0; i < entryIds.length; i += batchSize) {
        const batch = entryIds.slice(i, i + batchSize);
        await Promise.all(batch.map(id => 
          base44.entities.RosterEntry.update(id, { attendance_status: 'refusal' })
        ));
        console.log(`✅ Marked ${Math.min(i + batchSize, entryIds.length)}/${entryIds.length} as refusal`);
        // Very long delay between batches to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Delay before bulk create
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create logs in smaller chunks to avoid bulk create limits
      if (logsToCreate.length > 0) {
        const logChunkSize = 50;
        for (let i = 0; i < logsToCreate.length; i += logChunkSize) {
          const chunk = logsToCreate.slice(i, i + logChunkSize);
          await base44.entities.AttendanceLog.bulkCreate(chunk);
          if (i + logChunkSize < logsToCreate.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        // Mirror to AttendanceScanLog (audit-only)
        logAttendanceScanBulk(logsToCreate.map((l) => ({
          studentId: l.student_id,
          studentName: l.student_name,
          rosterType: 'ea',
          status: l.status,
          method: 'manual_entry',
        })));
        // Server-side hook: credit EA-present logs against ea_miss_trackings
        // so active EA cases resolve automatically.
        logsToCreate
          .filter((l) => l.status === 'present')
          .forEach((l) => resolveEaAttendance(l.student_id, l.log_date));
      }

      return pendingEntries.length;
    },
    onSuccess: (count) => {
      setMessage({ type: 'success', text: `${count} EA assignment(s) marked as REFUSAL (R). They remain on EA roster until closeout.` });
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || "Failed to process refusals" });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const eaCloseoutMutation = useMutation({
    mutationFn: async () => {
      console.log('🚀 Calling EA closeout function...');
      
      const response = await base44.functions.invoke('eaCloseout', {});
      
      if (!response.data?.success) {
        throw new Error(response.error || response.data?.error || 'EA closeout failed');
      }
      
      console.log('✅ EA closeout complete!');
      // Unwrap: response.data = {success: true, data: closeout_doc}
      return response.data.data;
    },
    onSuccess: (data) => {
      const successMsg = `✅ EA CLOSEOUT SUCCESSFUL!\n\n` +
        `EA Final Counts: ${data.eaPresent}P + ${data.eaExcused}E + ${data.eaAbsent}A + ${data.eaRefusal}R = ${data.eaPEA} PEA\n\n` +
        `✅ ${data.copiedToASD} Refusals copied to ASD\n` +
        `✅ ${data.resetCount} EA students reset to Pending\n` +
        `✅ ${data.duplicatesRemoved} ASD duplicates removed`;
      
      setMessage({
        type: 'success',
        text: successMsg
      });
      
      queryClient.invalidateQueries(['rosterEntries']);
      queryClient.invalidateQueries(['closeouts']);
      setShowEACloseoutConfirm(false);
      
      setTimeout(() => setMessage(null), 15000);
    },
    onError: (err) => {
      console.error('EA closeout error:', err);
      setMessage({ 
        type: 'error', 
        text: `❌ EA Closeout Failed: ${err.message || "Unknown error"}. Go to Settings → Restore Data to recover.` 
      });
      setShowEACloseoutConfirm(false);
      setTimeout(() => setMessage(null), 10000);
    }
  });

  const asdCloseoutMutation = useMutation({
    mutationFn: async () => {
      console.log('🚀 Calling ASD closeout function...');
      
      const response = await base44.functions.invoke('asdCloseout', {});
      
      if (!response.data?.success) {
        throw new Error(response.error || response.data?.error || 'ASD closeout failed');
      }
      
      console.log('✅ ASD closeout complete!');
      // Unwrap: response.data = {success: true, data: closeout_doc}
      return response.data.data;
    },
    onSuccess: (data) => {
      const successMsg = `✅ ASD CLOSEOUT SUCCESSFUL!\n\n` +
        `ASD Final Counts: ${data.asdPresent}P + ${data.asdAbsent}A + ${data.asdRefusal}R = ${data.asdPEA} PEA\n\n` +
        `✅ ${data.trackingUpdates} refusals tracked in EAMissTracking\n` +
        `✅ ${data.resetCount} ASD students reset to Pending\n` +
        `✅ ${data.duplicatesRemoved} ASD duplicates removed`;
      
      setMessage({
        type: 'success',
        text: successMsg
      });
      
      queryClient.invalidateQueries(['rosterEntries']);
      queryClient.invalidateQueries(['closeouts']);
      queryClient.invalidateQueries(['eaMissTracking']);
      setShowASDCloseoutConfirm(false);
      
      setTimeout(() => setMessage(null), 15000);
    },
    onError: (err) => {
      console.error('ASD closeout error:', err);
      setMessage({ 
        type: 'error', 
        text: `❌ ASD Closeout Failed: ${err.message || "Unknown error"}. Go to Settings → Restore Data to recover.` 
      });
      setShowASDCloseoutConfirm(false);
      setTimeout(() => setMessage(null), 10000);
    }
  });

  const getStudentCounts = () => {
    const studentMap = new Map();
    
    eaEntries.forEach(entry => {
      const otherId = entry.other_id;
      
      let entryStatus;
      if (entry.status === 'excused') {
        entryStatus = 'excused';
      } else {
        entryStatus = entry.attendance_status || 'pending';
      }
      
      const priority = {
        'present': 5,
        'refusal': 4,
        'absent': 3,
        'excused': 2,
        'pending': 1
      };
      
      const currentHighestStatus = studentMap.get(otherId);
      
      if (!currentHighestStatus || priority[entryStatus] > priority[currentHighestStatus]) {
        studentMap.set(otherId, entryStatus);
      }
    });
    
    let pendingCount = 0;
    let presentCount = 0;
    
    studentMap.forEach(status => {
      if (status === 'pending') pendingCount++;
      else if (status === 'present') presentCount++;
    });
    
    return { pendingCount, presentCount };
  };

  const counts = getStudentCounts();
  const pendingCount = counts.pendingCount;
  const presentCount = counts.presentCount;

  return (
    <>
    {/* Inline Kiosk Mode — mirrors the /kiosk experience but reuses the
        already-authenticated scanPresentMutation so admins don't need a PIN.
        The existing welcomeBanner state already auto-clears after 2s. */}
    {isKioskMode ? (
      <div className="fixed inset-0 z-[9990] bg-slate-900 flex flex-col" data-testid="attendance-kiosk-mode">
        <div className="flex justify-end p-3">
          <Button
            onClick={() => setIsKioskMode(false)}
            variant="outline"
            size="sm"
            className="bg-white/90 hover:bg-white text-slate-900"
            data-testid="kiosk-exit"
          >
            Exit Kiosk
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          {welcomeBanner && welcomeBanner.name && (
            <div
              data-testid="kiosk-welcome-banner"
              className="max-w-3xl w-full text-center bg-gradient-to-br from-green-500 to-emerald-700 text-white rounded-3xl shadow-2xl p-12"
            >
              <CheckCircle className="w-24 h-24 mx-auto mb-6" strokeWidth={2.5} />
              <p className="text-5xl font-extrabold tracking-tight mb-4">
                Welcome, {welcomeBanner.name}!
              </p>
              <p className="text-xl opacity-90 font-mono">ID {welcomeBanner.otherId} · Marked Present</p>
            </div>
          )}
          {welcomeBanner && welcomeBanner.error && (
            <div
              data-testid="kiosk-not-found-banner"
              className="max-w-3xl w-full text-center bg-red-700 text-white rounded-3xl shadow-2xl p-12"
            >
              <AlertCircle className="w-24 h-24 mx-auto mb-6" strokeWidth={2.5} />
              <p className="text-5xl font-extrabold tracking-tight mb-4 uppercase">Not Found</p>
              <p className="text-2xl leading-relaxed">
                Please Sit at Table 1 (First Table) for Assistance.
              </p>
            </div>
          )}
          {!welcomeBanner && (
            <div className="max-w-lg w-full">
              <Card className="border-none shadow-2xl">
                <CardContent className="p-10 space-y-6 text-center">
                  <ScanLine className="w-16 h-16 mx-auto text-blue-600 animate-pulse" />
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900">Scan Your Badge</h1>
                    <p className="text-slate-500 mt-2">or type your 6-digit ID</p>
                  </div>
                  <form onSubmit={handleScanSubmit}>
                    <Input
                      ref={scanInputRef}
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onBlur={() => setTimeout(() => scanInputRef.current?.focus(), 100)}
                      placeholder="000000"
                      className="h-16 text-2xl text-center font-mono"
                      disabled={scanPresentMutation.isPending}
                      autoFocus
                      data-testid="kiosk-inline-input"
                    />
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    ) : null}

    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">EA Attendance</h1>
            <p className="text-slate-600 mt-1">Mark Eagle Academy Attendance</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setIsKioskMode(true)}
              variant="outline"
              className="border-slate-300 hover:bg-slate-100 shadow"
              size="lg"
              data-testid="kiosk-mode-toggle"
            >
              <ScanLine className="w-4 h-4 mr-2" />
              Kiosk Mode
            </Button>
            <Button
              onClick={() => setShowEACloseoutConfirm(true)}
              className="bg-blue-700 hover:bg-blue-800 shadow-lg"
              size="lg">
              <Lock className="w-4 h-4 mr-2" />
              EA Closeout
            </Button>
            <Button
              onClick={() => setShowASDCloseoutConfirm(true)}
              className="bg-red-800 hover:bg-red-900 shadow-lg"
              size="lg">
              <Lock className="w-4 h-4 mr-2" />
              ASD Closeout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-700 to-red-800 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">EA Pending</p>
                  <p className="text-3xl font-bold">{pendingCount}</p>
                </div>
                <QrCode className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">EA Present Today</p>
                  <p className="text-3xl font-bold">{presentCount}</p>
                </div>
                <CheckCircle className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <X className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="scan" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="scan" data-testid="tab-scan">
              <ScanLine className="w-4 h-4 mr-1" />
              Scan
            </TabsTrigger>
            <TabsTrigger value="bulk-present" data-testid="tab-bulk-present">Bulk Present</TabsTrigger>
            <TabsTrigger value="bulk-absent" data-testid="tab-bulk-absent">Bulk Absent</TabsTrigger>
            <TabsTrigger value="bulk-excused" data-testid="tab-bulk-excused">Bulk Excused</TabsTrigger>
            <TabsTrigger value="refusal" data-testid="tab-refusal">Process Rs</TabsTrigger>
          </TabsList>

          <TabsContent value="scan">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ScanLine className="w-5 h-5" />
                  Barcode Scanner Check-In
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-sm text-blue-900">
                    <strong>📷 Scanner Instructions:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Point your USB / Bluetooth barcode scanner at a student ID badge</li>
                      <li>The scanned ID appears in the field and is submitted on Enter</li>
                      <li>The student is instantly marked <strong>PRESENT</strong> and makeup days advance</li>
                      <li>You can also type an ID manually and press Enter</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <form onSubmit={handleScanSubmit} className="flex gap-2">
                  <Input
                    ref={scanInputRef}
                    data-testid="scan-input"
                    autoFocus
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    onBlur={() => setTimeout(() => scanInputRef.current?.focus(), 100)}
                    placeholder={welcomeBanner ? "" : "Ready to scan — point scanner at student ID badge"}
                    className="h-14 text-lg font-mono"
                    disabled={scanPresentMutation.isPending || !!welcomeBanner}
                  />
                  <Button
                    type="submit"
                    data-testid="scan-submit-button"
                    className="h-14 px-6 bg-green-600 hover:bg-green-700"
                    disabled={!scanInput.trim() || scanPresentMutation.isPending}
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Mark Present
                  </Button>
                </form>

                {welcomeBanner && welcomeBanner.name && (
                  <div
                    data-testid="scan-welcome-banner"
                    className="rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 text-white p-8 shadow-2xl animate-in fade-in zoom-in duration-200"
                  >
                    <div className="flex items-center gap-4">
                      <CheckCircle className="w-16 h-16 flex-shrink-0" strokeWidth={2.5} />
                      <div>
                        <p className="text-4xl font-bold tracking-tight">
                          Welcome, {welcomeBanner.name}!
                        </p>
                        <p className="text-lg opacity-90 mt-1 font-mono">
                          ID {welcomeBanner.otherId} · Marked Present
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {welcomeBanner && welcomeBanner.error && (
                  <div
                    data-testid="scan-error-banner"
                    className="rounded-2xl bg-gradient-to-br from-red-500 to-red-700 text-white p-8 shadow-2xl animate-in fade-in zoom-in duration-200"
                  >
                    <div className="flex items-center gap-4">
                      <AlertCircle className="w-16 h-16 flex-shrink-0" strokeWidth={2.5} />
                      <div>
                        <p className="text-3xl font-bold tracking-tight">Scan Failed</p>
                        <p className="text-lg opacity-90 mt-1">{welcomeBanner.error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {!welcomeBanner && !scanPresentMutation.isPending && (
                  <div
                    data-testid="scan-ready-state"
                    className="rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/50 p-6 text-center"
                  >
                    <ScanLine className="w-10 h-10 text-blue-500 mx-auto mb-2 animate-pulse" />
                    <p className="text-blue-900 font-medium">Ready to scan</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Point your scanner at a student badge — check-in happens automatically
                    </p>
                  </div>
                )}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-semibold text-slate-700 mb-2">
                    Recent Scans ({scanHistory.length})
                  </p>
                  {scanHistory.length === 0 ? (
                    <p className="text-sm text-slate-500">Scan a badge to see history here</p>
                  ) : (
                    <ul className="space-y-1 max-h-64 overflow-y-auto" data-testid="scan-history-list">
                      {scanHistory.map((s, idx) => (
                        <li
                          key={idx}
                          data-testid={`scan-history-item-${idx}`}
                          className={`text-sm flex items-center gap-2 p-2 rounded ${
                            s.status === 'ok' ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'
                          }`}
                        >
                          {s.status === 'ok' ? (
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          )}
                          <span className="font-mono text-xs">{format(s.ts, 'HH:mm:ss')}</span>
                          <span className="font-mono">{s.otherId}</span>
                          <span className="flex-1">
                            {s.status === 'ok' ? `→ ${s.studentName} marked Present` : `→ ${s.message}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bulk-present">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Bulk Mark Present
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-sm text-green-900">
                    <strong>📋 Bulk Present Instructions:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Paste <strong>Other IDs</strong> from another system</li>
                      <li>One ID per line</li>
                      <li>All EA students will be marked as PRESENT</li>
                      <li>✅ Makeup days will be tracked automatically.</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <div>
                  <Textarea
                    value={bulkPresentIds}
                    onChange={(e) => setBulkPresentIds(e.target.value)}
                    placeholder="Enter Other IDs, one per line..."
                    className="h-64 font-mono"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    Paste or type <strong>Other IDs</strong>, each on a new line
                  </p>
                </div>
                <Button
                  onClick={() => markPresentBulkMutation.mutate(bulkPresentIds)}
                  className="w-full bg-green-600 hover:bg-green-700 h-12"
                  disabled={!bulkPresentIds.trim() || markPresentBulkMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark All as Present
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bulk-absent">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Bulk Mark Absent
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertDescription className="text-sm text-yellow-900">
                    <strong>📋 Bulk Absent Instructions:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Paste <strong>Other IDs</strong> of absent students</li>
                      <li>One ID per line</li>
                      <li>All EA students will be marked as ABSENT</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <div>
                  <Textarea
                    value={bulkAbsentIds}
                    onChange={(e) => setBulkAbsentIds(e.target.value)}
                    placeholder="Enter Other IDs, one per line..."
                    className="h-64 font-mono"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    Paste or type <strong>Other IDs</strong>, each on a new line
                  </p>
                </div>
                <Button
                  onClick={() => markAbsentBulkMutation.mutate(bulkAbsentIds)}
                  className="w-full bg-red-700 hover:bg-red-800 h-12"
                  disabled={!bulkAbsentIds.trim() || markAbsentBulkMutation.isPending}
                >
                  Mark All as Absent
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bulk-excused">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Bulk Mark Excused
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-orange-50 border-orange-200">
                  <AlertDescription className="text-sm text-orange-900">
                    <strong>📋 Bulk Excused Instructions:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Paste <strong>Student Emails</strong> from external excusal form</li>
                      <li>One email per line</li>
                      <li>All EA students will be marked as <strong>EXCUSED</strong></li>
                      <li>Students marked as excused will remain on EA roster until closeout</li>
                      <li>They will NOT be moved to ASD roster</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <div>
                  <Textarea
                    value={bulkExcusedEmails}
                    onChange={(e) => setBulkExcusedEmails(e.target.value)}
                    placeholder="Enter student emails, one per line..."
                    className="h-64 font-mono"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    Paste or type <strong>student emails</strong>, each on a new line
                  </p>
                </div>
                <Button
                  onClick={() => markExcusedBulkMutation.mutate(bulkExcusedEmails)}
                  className="w-full bg-orange-600 hover:bg-orange-700 h-12"
                  disabled={!bulkExcusedEmails.trim() || markExcusedBulkMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark All as Excused
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="refusal">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle>Process Remaining Pending as Refusals (Rs)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-orange-50 border-orange-200">
                  <AlertDescription className="text-orange-900">
                    <strong>Step 1:</strong> This will mark all EA students with &quot;pending&quot; attendance as REFUSAL (R). They will remain on the EA roster until closeout.
                  </AlertDescription>
                </Alert>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-700">
                    <strong>Current EA Pending Students:</strong> {pendingCount}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    After clicking this button, review your EA roster to confirm P, E, A, R counts before closeout.
                  </p>
                </div>
                <Button
                  onClick={() => processRefusalsMutation.mutate()}
                  className="w-full bg-red-800 hover:bg-red-900 h-12"
                  disabled={processRefusalsMutation.isPending || pendingCount === 0}
                >
                  Mark All Pending as Refusal (R)
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {showEACloseoutConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-lg">
              <CardHeader className="border-b bg-blue-50">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Lock className="w-5 h-5" />
                  Confirm EA Closeout
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-900">
                    <strong>📋 EA Closeout will:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Save snapshot of current EA roster</li>
                      <li>Record final P, E, A, R counts</li>
                      <li>Remove Present & Excused (mark completed)</li>
                      <li>Copy Refusals (Rs) to ASD roster</li>
                      <li>Reset Absent & Refusal to pending</li>
                      <li>Remove ASD duplicates</li>
                    </ul>
                    <p className="mt-2 text-sm font-semibold">⏱️ Takes 1-2 minutes</p>
                  </AlertDescription>
                </Alert>
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowEACloseoutConfirm(false)}
                    className="flex-1"
                    disabled={eaCloseoutMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => eaCloseoutMutation.mutate()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={eaCloseoutMutation.isPending}
                  >
                    {eaCloseoutMutation.isPending ? "Processing..." : "Confirm EA Closeout"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {showASDCloseoutConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-lg">
              <CardHeader className="border-b bg-red-50">
                <CardTitle className="flex items-center gap-2 text-red-900">
                  <Lock className="w-5 h-5" />
                  Confirm ASD Closeout
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Alert className="bg-red-50 border-red-200">
                  <AlertDescription className="text-red-900">
                    <strong>📋 ASD Closeout will:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Save snapshot of current ASD roster</li>
                      <li>Record final P, A, R counts</li>
                      <li>Track refusals in EAMissTracking</li>
                      <li>Remove Present & Excused (mark completed)</li>
                      <li>Reset Absent & Refusal to pending</li>
                      <li>Remove ASD duplicates</li>
                    </ul>
                    <p className="mt-2 text-sm font-semibold">⏱️ Takes 1-2 minutes</p>
                  </AlertDescription>
                </Alert>
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowASDCloseoutConfirm(false)}
                    className="flex-1"
                    disabled={asdCloseoutMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => asdCloseoutMutation.mutate()}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    disabled={asdCloseoutMutation.isPending}
                  >
                    {asdCloseoutMutation.isPending ? "Processing..." : "Confirm ASD Closeout"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
    </>
  );
}